import {
  OreStack,
  OreTableCell,
  OreTableRow,
  OreText,
  OreTooltip,
} from '@edenredespana/oreneta'
import { useLayoutEffect, useRef, useState } from 'react'

import { currency } from '@/Shared/domain'
import { formatTimeHM } from '@/Shared/domain/helpers/hours'
import { TransactionModel } from '@/Transactions/domain'
import { useUserData } from '@/User/ui/context'

interface IProps {
  transaction: TransactionModel
  isNursery?: boolean
  // Permite ajustar columnas cuando la fila se renderiza en Home.
  isHome?: boolean
  isIndependent?: boolean
}

export const TransactionRow = ({
  transaction,
  isNursery,
  isHome,
  isIndependent,
}: IProps): JSX.Element => {
  const { merchant } = useUserData()

  const {
    merchantId,
    transactionDateWithoutHour,
    transactionHour,
    transactionLiquidationDateWithoutHour,
    transactionLiquidationHour,
    merchantName,
    transactionAmount,
    invoiceNumber,
    productName,
    subProductName,
    childName,
  } = transaction

  // Componente que muestra texto truncado a 2 líneas (ellipsis) y,
  // sólo si realmente se está truncando, envuelve el contenido en un tooltip
  // con el texto completo.
  const TwoLineEllipsisText = ({ text }: { text?: string }) => {
    // Referencia al <span> que aplica el line-clamp de 2 líneas.
    const spanRef = useRef<HTMLSpanElement | null>(null)
    // Estado que indica si el contenido excede 2 líneas (si hay truncado).
    const [isOverflow, setIsOverflow] = useState(false)
    // Contenedor oculto reutilizable para medir alturas sin clamp.
    const measureContainerRef = useRef<HTMLDivElement | null>(null)
    // RAF para agrupar re-medidas y evitar medir demasiadas veces.
    const rafIdRef = useRef<number | null>(null)
    // Observer para limpiar correctamente.
    const roRef = useRef<ResizeObserver | null>(null)

    useLayoutEffect(() => {
      const el = spanRef.current
      if (!el || typeof window === 'undefined') return

      // Crear contenedor oculto una sola vez y reutilizarlo en cada medición.
      const container = document.createElement('div')
      measureContainerRef.current = container
      container.style.position = 'absolute'
      container.style.visibility = 'hidden'
      container.style.pointerEvents = 'none'
      container.style.whiteSpace = 'normal'
      container.style.wordBreak = 'break-word'
      container.style.boxSizing = 'border-box'
      document.body.appendChild(container)

      // Función de medición: calcula si el texto ocupa más de dos líneas
      // sin aplicar clamp, comparando altura natural vs. altura de dos líneas.
      const measure = () => {
        const element = spanRef.current
        const c = measureContainerRef.current
        if (!element || !c) return

        const style = window.getComputedStyle(element)
        const width =
          element.getBoundingClientRect().width ||
          element.clientWidth ||
          element.offsetWidth
        c.style.width = `${Math.ceil(width)}px`
        c.style.fontFamily = style.fontFamily
        c.style.fontSize = style.fontSize
        c.style.fontWeight = style.fontWeight as string
        c.style.lineHeight = style.lineHeight
        c.style.letterSpacing = style.letterSpacing

        // Altura natural del texto (sin clamp)
        const trimmed = (text || '').trim()
        c.textContent = trimmed
        const naturalHeight = c.scrollHeight

        // Altura de dos líneas: si la line-height está en px, calculamos rápido.
        // Si no, medimos con dos líneas de texto.
        let twoLineHeight: number
        const lh = style.lineHeight
        const isPx = lh.endsWith('px')
        const pxValue = isPx ? Number.parseFloat(lh) : NaN
        if (Number.isFinite(pxValue)) {
          twoLineHeight = Math.round(pxValue * 2)
        } else {
          c.innerHTML = 'W<br/>W'
          twoLineHeight = c.scrollHeight
          c.textContent = trimmed
        }

        const overflowClamped = naturalHeight - twoLineHeight > 0.5
        setIsOverflow(overflowClamped)
      }

      // Agrupar mediciones en el próximo frame para evitar repeticiones.
      const scheduleMeasure = () => {
        if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = requestAnimationFrame(measure)
      }

      // Medición inicial tras el layout.
      scheduleMeasure()
      // Re-medición cuando las fuentes están listas (si el navegador lo soporta).
      if ((document as any).fonts?.ready) {
        ;(document as any).fonts.ready.then(scheduleMeasure).catch(() => {})
      }

      // Observadores de cambio de tamaño: elemento, su padre y la celda de tabla.
      // Así reaccionamos a cambios de ancho del contenedor.
      const ro = new ResizeObserver(() => scheduleMeasure())
      ro.observe(el)
      if (el.parentElement) ro.observe(el.parentElement)
      const nearestCell = el.closest(
        'td, [role="cell"],[data-ore-table-cell]',
      ) as Element | null
      if (nearestCell) ro.observe(nearestCell)
      roRef.current = ro

      // También escuchar cambios de tamaño de la ventana.
      const onWindowResize = () => scheduleMeasure()
      window.addEventListener('resize', onWindowResize)

      return () => {
        if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current)
        if (roRef.current) roRef.current.disconnect()
        if (measureContainerRef.current) {
          try {
            document.body.removeChild(measureContainerRef.current)
          } catch {}
          measureContainerRef.current = null
        }
        window.removeEventListener('resize', onWindowResize)
      }
    }, [text])

    // Contenido visual: texto tipográfico y <span> con CSS para clamping a 2 líneas.
    const content = (
      <OreText size="text-sm">
        <span
          ref={spanRef}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {(text || '').trim()}
        </span>
      </OreText>
    )
    // Si hay truncado real (overflow), mostrar tooltip con el texto completo;
    // si no, renderizar el contenido tal cual sin tooltip.
    return isOverflow ? (
      <OreTooltip text={text || ''} placement="top" separation="small">
        {content}
      </OreTooltip>
    ) : (
      content
    )
  }

  return (
    <OreTableRow valign="middle">
      <OreTableCell>
        <OreStack space="small">
          <OreText size="text-sm">{merchantId}</OreText>
        </OreStack>
      </OreTableCell>
      {!isNursery ? (
        <OreTableCell>
          <OreStack space="2xsmall">
            <OreText size="text-sm">{transactionDateWithoutHour}</OreText>
            <OreText size="text-sm">{formatTimeHM(transactionHour)}h</OreText>
          </OreStack>
        </OreTableCell>
      ) : // En Home ocultamos esta celda para que coincida con la cabecera resumida.
      !merchant.isChild && !isHome && isNursery && !isIndependent ? (
        <OreTableCell>
          <OreStack space="small">
            <TwoLineEllipsisText text={merchantName} />
          </OreStack>
        </OreTableCell>
      ) : null}

      <OreTableCell>
        <OreStack space="2xsmall">
          <OreText size="text-sm">{transactionLiquidationDateWithoutHour}</OreText>
          <OreText size="text-sm">{formatTimeHM(transactionLiquidationHour)}h</OreText>
        </OreStack>
      </OreTableCell>
      <OreTableCell>
        <OreStack space="small">
          <OreText size="text-sm">{!isNursery ? merchantName : childName}</OreText>
        </OreStack>
      </OreTableCell>
      <OreTableCell>
        <OreStack space="small">
          <OreText size="text-sm">{currency(transactionAmount)}</OreText>
        </OreStack>
      </OreTableCell>
      <OreTableCell>
        <OreStack space="small">
          <OreText size="text-sm">{invoiceNumber}</OreText>
        </OreStack>
      </OreTableCell>
      {!isNursery ? (
        <>
          <OreTableCell>
            <OreStack space="small">
              <OreText size="text-sm">{productName}</OreText>
            </OreStack>
          </OreTableCell>
          <OreTableCell>
            <OreStack space="small">
              <OreText size="text-sm">{subProductName}</OreText>
            </OreStack>
          </OreTableCell>
        </>
      ) : null}
    </OreTableRow>
  )
}
